import React from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";

import { variationPercent } from "../btpSummary";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const WaterFootprintGraph = ({ gameStatus, phaseFocus = null }) => {
  // Extraction des scores
  const waterScore = gameStatus.score_dic.water.value;
  const blueWater = gameStatus.score_dic["blue-water"].value;
  const greenWater = gameStatus.score_dic["green-water"].value;
  const greyWater = gameStatus.score_dic["grey-water"].value;

  // Données pour les phases
  const phases = [
    {
      name: "Matériaux",
      icon: "/icon/materiaux.png",
      standard: gameStatus.score_dic["materiaux-waterfootprint"]?.standard_value ?? 0,
      measures: gameStatus.score_dic["materiaux-waterfootprint"]?.value ?? 0,
    },
    {
      name: "Chantier",
      icon: "/icon/chantier.png",
      standard: gameStatus.score_dic["chantier-waterfootprint"]?.standard_value ?? 0,
      measures: gameStatus.score_dic["chantier-waterfootprint"]?.value ?? 0,
    },
    {
      name: "Exploitation",
      icon: "/icon/exploitation.png",
      standard: gameStatus.score_dic["exploitation-waterfootprint"]?.standard_value ?? 0,
      measures: gameStatus.score_dic["exploitation-waterfootprint"]?.value ?? 0,
    },
  ];
  const maxGlobalValue = Math.max(
    ...phases.flatMap(phase => [phase.standard, phase.measures])
  );

  const globalWaterFootprintStandard = phases.reduce((sum, item) => sum + item.standard, 0);
  const globalWaterFootprint = phases.reduce((sum, item) => sum + item.measures, 0);
  // « par rapport au projet standard » : la base est le standard (btpSummary.js).
  const variation = variationPercent(globalWaterFootprint, globalWaterFootprintStandard) ?? 0;

  const formatValue = (value) => {
    const formattedValue = value > 1 ? value.toFixed(0) : value.toFixed(2);
    return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const phase = phaseFocus 
    ? phases.find(p => p.name.toLowerCase().includes(phaseFocus.split('-')[0])) || phases[0]
    : null;
  const phaseVariation = phase ? (variationPercent(phase.measures, phase.standard) ?? 0) : 0;

  return (
    <div className="water-footprint-container">
      <div className="water-footprint-summary">
        {phase && (
          <div className="water-footprint-global">
            <div className="water-footprint-title">Empreinte Eau {phase.name}</div>
            <div className="water-footprint-header">
              <div className="water-footprint-header-content">
                <img
                  className="water-footprint-icon"
                  src={phase.icon}
                  alt={`Icone ${phase.name}`}
                />
                <div className="water-footprint-score">{formatValue(phase.measures)} m³</div>
              </div>
              <div className="water-footprint-detail-1">{phaseVariation}% d'impact</div>
              <div className="water-footprint-detail-2">
                par rapport au projet standard (WF : {formatValue(phase.standard)} m³)
              </div>
            </div>
          </div>
        )}
        {!phase && (<div className="water-footprint-global">
          <div className="water-footprint-title">Empreinte Eau Globale</div>
          <div className="water-footprint-header">
            <div className="water-footprint-header-content">
              <img
                className="water-footprint-icon"
                src="/icon/water-footprint.png"
                alt="Icone Eau"
              />
              <div className="water-footprint-score">{formatValue(globalWaterFootprint)} m³</div>
            </div>
            <div className="water-footprint-detail-1">{variation}% d'impact</div>
            <div className="water-footprint-detail-2">
              par rapport au projet standard (WF : {formatValue(globalWaterFootprintStandard)} m³)
            </div>
          </div>
        </div>)}

        <div className="water-footprint-graph">
          <div className="water-footprint-title">Détail par phase</div>
          <div className="water-footprint-legend">
            <div className="water-footprint-legend-icon" style={{backgroundColor:'#3f5760'}}></div>
            <div className="water-footprint-legend-text">Projet standard</div>
            <div className="water-footprint-legend-icon" style={{backgroundColor:'#3498db'}}></div>
            <div className="water-footprint-legend-text">Aquacité</div>
          </div>
          {phases.map((phase, index) => {
          const standardWidth = (phase.standard / maxGlobalValue) * 100;
          const measuresWidth = (phase.measures / maxGlobalValue) * 100;
          const isTransparent = 
            (phaseFocus === "chantier-waterfootprint" && phase.name !== "Chantier") ||
            (phaseFocus === "materiaux-waterfootprint" && phase.name !== "Matériaux") ||
            (phaseFocus === "exploitation-waterfootprint" && phase.name !== "Exploitation");

          return (
            <div
              key={index}
              className="water-footprint-phase-figures"
              style={{ opacity: isTransparent ? 0.5 : 1 }}
            >
              <div className="water-footprint-phase-title">{phase.name}</div>
              <div key={index} className="water-footprint-phase">
                
                {/* Icône de la phase */}
                <img className="water-footprint-phase-icon" src={phase.icon} alt={phase.name} />

                {/* Conteneur des barres */}
                <div className="water-footprint-phase-bars">
                  <div className="water-footprint-bar-container">
                    <div
                      className="water-footprint-bar water-footprint-bar-standard"
                      style={{ width: `${standardWidth}%` }}
                    ></div>
                    <span className="water-footprint-value">{formatValue(phase.standard)} m³</span>
                  </div>
                  <div className="water-footprint-bar-container">
                    <div
                      className="water-footprint-bar water-footprint-bar-measures"
                      style={{ width: `${measuresWidth}%` }}
                    ></div>
                    <span className="water-footprint-value">{formatValue(phase.measures)} m³</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        </div>
      </div>
    </div>
  );
};

export default WaterFootprintGraph;